import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { formatCurrency, formatDate, formatDateTime, getStatusColor, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Crown, Loader2, CreditCard, Plus, History, DollarSign, Calendar, MoreHorizontal, RotateCcw, ShieldAlert, Pause } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [landlordPayments, setLandlordPayments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: '',
    months_extended: 1
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subsRes, paymentsRes] = await Promise.all([
        adminAPI.getSubscriptions(),
        adminAPI.getSubscriptionPayments()
      ]);
      setSubscriptions(subsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedSub || !paymentForm.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await adminAPI.recordSubscriptionPayment({
        landlord_id: selectedSub.landlord_id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        notes: paymentForm.notes || null,
        months_extended: paymentForm.months_extended
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: '', payment_method: 'bank_transfer', reference_number: '', notes: '', months_extended: 1 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewHistory = async (sub) => {
    setSelectedSub(sub);
    try {
      const response = await adminAPI.getLandlordPayments(sub.landlord_id);
      setLandlordPayments(response.data);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load payment history');
    }
  };

  const openRefundDialog = (sub) => {
    setSelectedSub(sub);
    setShowRefundDialog(true);
  };

  const handleRefund = async () => {
    if (!selectedSub) return;
    setSubmitting(true);
    try {
      const { data } = await adminAPI.refundStripeSubscription(selectedSub.landlord_id);
      toast.success(data.message || 'Refund recorded — process the cash refund in your Stripe dashboard.');
      setShowRefundDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund');
    } finally {
      setSubmitting(false);
    }
  };

  const openPaymentDialog = (sub) => {
    setSelectedSub(sub);
    setPaymentForm(prev => ({ ...prev, amount: (sub.amount || 9.99).toString() }));
    setShowPaymentDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const activeCount = subscriptions.filter(s => s.status === 'active').length;
  const pausedCount = subscriptions.filter(s => s.status === 'paused').length;
  const totalRevenue = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const thisMonthPayments = payments.filter(p => {
    const payDate = new Date(p.created_at);
    const now = new Date();
    return payDate.getMonth() === now.getMonth() && payDate.getFullYear() === now.getFullYear();
  });
  const monthlyRevenue = thisMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  const getSubStatusBadge = (sub) => {
    if (sub.status === 'paused') return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Paused</Badge>;
    if (sub.is_trial && sub.trial_expired) return <Badge className="bg-red-100 text-red-800 border-red-300">Trial Expired</Badge>;
    if (sub.is_trial) return <Badge className="bg-blue-100 text-blue-800 border-blue-300">Trial</Badge>;
    if (sub.status === 'active') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Active</Badge>;
    return <Badge className={getStatusColor(sub.status)}>{sub.status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">Subscription Management</h1>
        <p className="text-muted-foreground mt-1">Manage landlord subscriptions and payments</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Crown className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Pause className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Paused</p>
                <p className="text-2xl font-bold">{pausedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">{formatCurrency(monthlyRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <p className="text-2xl font-bold">{payments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="subscriptions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscriptions">Active Subscriptions</TabsTrigger>
          <TabsTrigger value="payments">Payment History</TabsTrigger>
        </TabsList>

        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5" />
                All Subscriptions ({subscriptions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No subscriptions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Landlord</th>
                        <th>Tier</th>
                        <th>Amount</th>
                        <th>Limits</th>
                        <th>End Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => {
                        const isExpiringSoon = new Date(sub.end_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                        return (
                          <tr key={sub.subscription_id || sub.landlord_id}>
                            <td>
                              <div className="flex items-center gap-3">
                                <Avatar className="w-9 h-9">
                                  <AvatarImage src={sub.landlord?.picture} />
                                  <AvatarFallback className="bg-[#C85A42] text-white text-sm">
                                    {getInitials(sub.landlord?.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{sub.landlord?.name || 'Unknown'}</p>
                                  <p className="text-xs text-muted-foreground">{sub.landlord?.email}</p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <Badge variant="outline" className="capitalize">
                                {sub.tier}
                              </Badge>
                            </td>
                            <td className="font-medium">{formatCurrency(sub.amount)}</td>
                            <td className="text-sm text-muted-foreground">
                              {sub.property_limit === -1 ? '∞' : sub.property_limit} props, {sub.unit_limit === -1 ? '∞' : sub.unit_limit} units
                            </td>
                            <td>
                              <span className={isExpiringSoon ? 'text-red-600 font-medium' : ''}>
                                {formatDate(sub.end_date)}
                              </span>
                              {isExpiringSoon && (
                                <Badge variant="destructive" className="ml-2 text-xs">
                                  Expiring
                                </Badge>
                              )}
                            </td>
                            <td>
                              {getSubStatusBadge(sub)}
                            </td>
                            <td>
                              <div className="flex gap-1">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" data-testid={`manage-sub-${sub.landlord_id}`}>
                                      <MoreHorizontal className="w-4 h-4 mr-1" />
                                      Manage
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem onClick={() => openPaymentDialog(sub)} data-testid={`record-payment-${sub.landlord_id}`}>
                                      <Plus className="w-4 h-4 mr-2" />
                                      Record Payment
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleViewHistory(sub)} data-testid={`view-history-${sub.landlord_id}`}>
                                      <History className="w-4 h-4 mr-2" />
                                      Payment History
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => openRefundDialog(sub)}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`refund-sub-${sub.landlord_id}`}
                                    >
                                      <RotateCcw className="w-4 h-4 mr-2" />
                                      Refund (30-day)
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment History ({payments.length})
              </CardTitle>
              <CardDescription>All recorded subscription payments</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No payments recorded yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Landlord</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th>Months</th>
                        <th>Period</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.payment_id}>
                          <td>
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={payment.landlord?.picture} />
                                <AvatarFallback className="bg-[#C85A42] text-white text-xs">
                                  {getInitials(payment.landlord?.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{payment.landlord?.name || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="font-medium text-green-600">{formatCurrency(payment.amount)}</td>
                          <td className="capitalize">{payment.payment_method?.replace('_', ' ')}</td>
                          <td className="text-sm text-muted-foreground">{payment.reference_number || '-'}</td>
                          <td>{payment.months_extended} month(s)</td>
                          <td className="text-sm text-muted-foreground">
                            {formatDate(payment.period_start)} - {formatDate(payment.period_end)}
                          </td>
                          <td className="text-sm text-muted-foreground">{formatDateTime(payment.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Record Subscription Payment
            </DialogTitle>
            <DialogDescription>
              Record a payment for {selectedSub?.landlord?.name}'s subscription
            </DialogDescription>
          </DialogHeader>
          
          {selectedSub && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={selectedSub.landlord?.picture} />
                  <AvatarFallback className="bg-[#C85A42] text-white">
                    {getInitials(selectedSub.landlord?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedSub.landlord?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Current: {selectedSub.tier} ({formatCurrency(selectedSub.amount)}/mo)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <Input
                    type="text"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Months Extended</Label>
                  <Select 
                    value={paymentForm.months_extended.toString()} 
                    onValueChange={(val) => setPaymentForm(prev => ({ ...prev, months_extended: parseInt(val) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 6, 12].map(m => (
                        <SelectItem key={m} value={m.toString()}>{m} month{m > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(val) => setPaymentForm(prev => ({ ...prev, payment_method: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="gcash">GCash</SelectItem>
                    <SelectItem value="maya">Maya</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="Transaction ID or reference"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes (optional)"
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRecordPayment}
              disabled={submitting || !paymentForm.amount}
              className="bg-[#C85A42] hover:bg-[#B04A35]"
              data-testid="confirm-payment-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Payment History - {selectedSub?.landlord?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto">
            {landlordPayments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No payment history found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {landlordPayments.map((payment) => (
                  <div key={payment.payment_id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-green-600">{formatCurrency(payment.amount)}</span>
                      <Badge variant="outline" className="capitalize">
                        {payment.payment_method?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Period: {formatDate(payment.period_start)} - {formatDate(payment.period_end)}</p>
                      {payment.reference_number && <p>Ref: {payment.reference_number}</p>}
                      {payment.notes && <p>Notes: {payment.notes}</p>}
                      <p>Recorded: {formatDateTime(payment.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Refund Dialog (30-day money-back) */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-600" />
              Refund Subscription (30-day window)
            </DialogTitle>
            <DialogDescription>
              {selectedSub?.landlord?.name ? <>Refund <strong>{selectedSub.landlord.name}</strong>'s most recent paid payment.</> : 'Refund the most recent paid payment.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex gap-3">
              <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="space-y-1 text-sm text-red-800">
                <p className="font-medium">This action will:</p>
                <ul className="list-disc list-inside space-y-0.5 text-red-700">
                  <li>Mark the latest paid transaction as <code className="text-xs">refunded</code></li>
                  <li>End the subscription immediately (status → <code className="text-xs">canceled</code>)</li>
                  <li>Only succeeds if the payment is within the last 30 days</li>
                </ul>
                <p className="pt-2 font-medium">
                  You must still process the actual money refund in your Stripe dashboard.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)} data-testid="cancel-refund-btn">Cancel</Button>
            <Button onClick={handleRefund} disabled={submitting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-refund-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Refund & Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
